import { z } from 'zod';
import { trainingCommandIdSchema } from './training-content.contract.js';
export const trainingCertificationsCapability = 'training.certifications.v1';
export const trainingCertificationIdSchema = z.string().uuid();
export const trainingCertificationLifecycleStatusSchema = z.enum([
    'current',
    'superseded',
    'revoked',
]);
export const trainingCertificationEffectiveStatusSchema = z.enum([
    'active',
    'expiring',
    'expired',
    'non-expiring',
    'superseded',
    'revoked',
]);
export const trainingCertificationEvidenceKindSchema = z.enum([
    'tenant-document',
    'external-registry',
    'manual-review',
]);
const trainingCertificationDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, 'Certification dates must be valid calendar dates.');
export const trainingCertificationEvidenceSchema = z.strictObject({
    kind: trainingCertificationEvidenceKindSchema,
    reference: z.string().trim().min(1).max(500).refine((value) => !/[\u0000-\u001f\u007f]/.test(value), 'Certification evidence references cannot contain control characters.'),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    capturedAt: z.string().datetime({ offset: true }),
});
export const trainingCertificationSchema = z.strictObject({
    id: trainingCertificationIdSchema,
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    chainRootId: trainingCertificationIdSchema,
    renewedFromId: trainingCertificationIdSchema.nullable(),
    supersededById: trainingCertificationIdSchema.nullable(),
    certificationName: z.string().trim().min(2).max(240),
    issuerName: z.string().trim().min(2).max(240),
    credentialNumber: z.string().trim().min(1).max(240).nullable(),
    issuedOn: trainingCertificationDateSchema,
    expiresOn: trainingCertificationDateSchema.nullable(),
    evidence: trainingCertificationEvidenceSchema.nullable(),
    recordBasis: z.literal('tenant-attested'),
    issuerVerification: z.literal('not-verified-by-crest'),
    lifecycleStatus: trainingCertificationLifecycleStatusSchema,
    effectiveStatus: trainingCertificationEffectiveStatusSchema,
    revision: z.number().int().positive(),
    recordedByMembershipId: z.string().uuid(),
    revokedByMembershipId: z.string().uuid().nullable(),
    revokedAt: z.string().datetime({ offset: true }).nullable(),
    revokedReason: z.string().trim().min(10).max(1_000).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((value, context) => {
    if (value.expiresOn !== null && value.expiresOn < value.issuedOn) {
        context.addIssue({
            code: 'custom', path: ['expiresOn'],
            message: 'Certification expiration cannot precede its issue date.',
        });
    }
    const revocationFields = [value.revokedAt, value.revokedByMembershipId, value.revokedReason];
    const revocationValid = value.lifecycleStatus === 'revoked'
        ? revocationFields.every((field) => field !== null)
        : revocationFields.every((field) => field === null);
    if (!revocationValid) {
        context.addIssue({
            code: 'custom', path: ['revokedAt'],
            message: 'Revoked certifications must retain actor, time, and reason.',
        });
    }
});
const trainingCertificationEvidenceInputSchema = trainingCertificationEvidenceSchema.nullable().default(null);
export const trainingRecordCertificationSchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    membershipId: z.string().uuid(),
    certificationName: z.string().trim().min(2).max(240),
    issuerName: z.string().trim().min(2).max(240),
    credentialNumber: z.string().trim().min(1).max(240).nullable().default(null),
    issuedOn: trainingCertificationDateSchema,
    expiresOn: trainingCertificationDateSchema.nullable().default(null),
    evidence: trainingCertificationEvidenceInputSchema,
    reason: z.string().trim().min(10).max(1_000),
}).superRefine((value, context) => {
    if (value.expiresOn !== null && value.expiresOn < value.issuedOn) {
        context.addIssue({
            code: 'custom', path: ['expiresOn'],
            message: 'Certification expiration cannot precede its issue date.',
        });
    }
});
export const trainingRenewCertificationSchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    credentialNumber: z.string().trim().min(1).max(240).nullable().default(null),
    issuedOn: trainingCertificationDateSchema,
    expiresOn: trainingCertificationDateSchema.nullable().default(null),
    evidence: trainingCertificationEvidenceInputSchema,
    reason: z.string().trim().min(10).max(1_000),
}).superRefine((value, context) => {
    if (value.expiresOn !== null && value.expiresOn < value.issuedOn) {
        context.addIssue({
            code: 'custom', path: ['expiresOn'],
            message: 'Certification expiration cannot precede its issue date.',
        });
    }
});
export const trainingRevokeCertificationSchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(1_000),
});
export const trainingCertificationsQuerySchema = z.strictObject({
    scope: z.enum(['mine', 'all']).default('mine'),
    includeHistory: z.boolean().default(false),
    membershipId: z.string().uuid().nullable().default(null),
    limit: z.number().int().min(1).max(200).default(100),
    offset: z.number().int().min(0).default(0),
}).superRefine((value, context) => {
    if (value.scope === 'mine' && value.membershipId !== null) {
        context.addIssue({
            code: 'custom', path: ['membershipId'],
            message: 'Mine-scoped certification queries cannot select another membership.',
        });
    }
});
export const trainingCertificationEventSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    certificationId: trainingCertificationIdSchema,
    relatedCertificationId: trainingCertificationIdSchema.nullable(),
    operation: z.enum(['record', 'renew', 'revoke']),
    revisionAfter: z.number().int().positive(),
    reason: z.string().trim().min(10).max(1_000),
    actorMembershipId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
const trainingCertificationCommandSchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    idempotent: z.boolean(),
    previousCertificationId: trainingCertificationIdSchema.nullable(),
});
export const trainingCertificationResponseSchema = z.strictObject({
    data: trainingCertificationSchema,
    command: trainingCertificationCommandSchema,
});
export const trainingCertificationsResponseSchema = z.strictObject({
    data: z.array(trainingCertificationSchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
export const trainingCertificationHistoryResponseSchema = z.strictObject({
    data: z.strictObject({
        certifications: z.array(trainingCertificationSchema),
        events: z.array(trainingCertificationEventSchema),
    }),
});
//# sourceMappingURL=training-certification.contract.js.map